import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, Users, ChevronLeft, Send, Settings2 } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import { useAuthStore } from '@/stores/auth.ts'
import {
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  Modal,
  Switch,
  useToast,
} from '@/components/ui'
import type { ColumnDef } from '@tanstack/react-table'

interface M365AppConnection {
  id: number
  display_name: string
  tenant_id: string
  client_id: string
  is_active: boolean
  last_test_at: string | null
  last_test_success: boolean | null
  last_test_error: string | null
  created_at: string
  updated_at: string
}

interface SharedMailbox {
  id: number
  display_name: string
  email_address: string
  is_active: boolean
  transport_type: 'graph' | 'smtp' | 'm365_app'
  smtp_host: string | null
  smtp_port: number | null
  smtp_username: string | null
  smtp_secure: boolean
  smtp_from_name: string | null
  smtp_has_password: boolean
  smtp_last_test_at: string | null
  smtp_last_test_success: boolean | null
  smtp_last_test_error: string | null
  m365_app_connection_id: number | null
  m365_mailbox_address: string | null
  m365_last_send_test_at: string | null
  m365_last_send_test_success: boolean | null
  m365_last_send_test_error: string | null
  created_at: string
}

interface MailboxPermission {
  id: number
  user_id: string
  user_name: string
  user_email: string
  can_read: boolean
  can_send: boolean
  granted_at: string
}

interface User {
  id: string
  name: string
  email: string
}

interface MailboxFormState {
  display_name: string
  email_address: string
  is_active: boolean
  transport_type: 'graph' | 'smtp' | 'm365_app'
  smtp_host: string
  smtp_port: string
  smtp_username: string
  smtp_password: string
  smtp_secure: boolean
  smtp_from_name: string
  m365_app_connection_id: string
  m365_mailbox_address: string
}

function emptyForm(mb?: SharedMailbox): MailboxFormState {
  return {
    display_name: mb?.display_name ?? '',
    email_address: mb?.email_address ?? '',
    is_active: mb?.is_active ?? true,
    transport_type: mb?.transport_type ?? 'graph',
    smtp_host: mb?.smtp_host ?? '',
    smtp_port: mb?.smtp_port?.toString() ?? '',
    smtp_username: mb?.smtp_username ?? '',
    smtp_password: '',
    smtp_secure: mb?.smtp_secure ?? true,
    smtp_from_name: mb?.smtp_from_name ?? '',
    m365_app_connection_id: mb?.m365_app_connection_id?.toString() ?? '',
    m365_mailbox_address: mb?.m365_mailbox_address ?? '',
  }
}

function MailboxFormModal({
  open, onClose, title, onSave, isPending, isEdit, form, setForm, editTarget, appConnections,
}: {
  open: boolean; onClose: () => void; title: string; onSave: () => void; isPending: boolean; isEdit: boolean
  form: MailboxFormState; setForm: (f: MailboxFormState) => void; editTarget: SharedMailbox | null
  appConnections: M365AppConnection[]
}) {
  return (
    <Modal open={open} onClose={onClose} title={title}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Input label="Anzeigename" value={form.display_name}
            onChange={(e) => setForm({ ...form, display_name: e.target.value })} />
          <Input label="E-Mail-Adresse" type="email" value={form.email_address}
            onChange={(e) => setForm({ ...form, email_address: e.target.value })} />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Versandart</label>
          <select
            className="w-full border rounded px-3 py-2 text-sm"
            value={form.transport_type}
            onChange={(e) => setForm({ ...form, transport_type: e.target.value as 'graph' | 'smtp' | 'm365_app' })}
          >
            <option value="graph">Microsoft 365 (delegiert, Benutzer-Token)</option>
            <option value="m365_app">Microsoft 365 (App-Permission, benutzerunabhängig)</option>
            <option value="smtp">SMTP</option>
          </select>
        </div>

        {form.transport_type === 'm365_app' && (
          <div className="border rounded-lg p-4 space-y-3 bg-blue-50/50">
            <h4 className="text-sm font-medium">Microsoft Graph App-Verbindung</h4>
            <div>
              <label className="block text-sm font-medium mb-1">App-Verbindung</label>
              <select
                className="w-full border rounded px-3 py-2 text-sm"
                value={form.m365_app_connection_id}
                onChange={(e) => setForm({ ...form, m365_app_connection_id: e.target.value })}
              >
                <option value="">Verbindung wählen…</option>
                {appConnections.filter((c) => c.is_active).map((c) => (
                  <option key={c.id} value={c.id.toString()}>{c.display_name}</option>
                ))}
              </select>
              {appConnections.length === 0 && (
                <p className="text-xs text-amber-700 mt-1">
                  Keine App-Verbindungen konfiguriert. Bitte zuerst eine M365-App-Verbindung anlegen.
                </p>
              )}
            </div>
            <Input
              label="Postfach-Adresse in Microsoft 365 (UPN)"
              placeholder="postfach@firma.de (leer = E-Mail-Adresse oben)"
              value={form.m365_mailbox_address}
              onChange={(e) => setForm({ ...form, m365_mailbox_address: e.target.value })}
            />
            <div className="text-xs text-muted-foreground space-y-1">
              <p>Die App-Verbindung muss in Exchange Online auf dieses Postfach berechtigt sein (Application Access Policy oder Exchange RBAC).</p>
            </div>
          </div>
        )}

        {form.transport_type === 'smtp' && (
          <div className="border rounded-lg p-4 space-y-3 bg-muted/20">
            <h4 className="text-sm font-medium">SMTP-Konfiguration</h4>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Input label="SMTP-Host" placeholder="mail.example.com" value={form.smtp_host}
                  onChange={(e) => setForm({ ...form, smtp_host: e.target.value })} />
              </div>
              <Input label="Port" type="number" placeholder="587" value={form.smtp_port}
                onChange={(e) => setForm({ ...form, smtp_port: e.target.value })} />
            </div>
            <Input label="Benutzername" placeholder="user@example.com" value={form.smtp_username}
              onChange={(e) => setForm({ ...form, smtp_username: e.target.value })} />
            <div>
              <Input
                label={isEdit && editTarget?.smtp_has_password ? 'Passwort (leer lassen um vorhandenes zu behalten)' : 'Passwort'}
                type="password"
                placeholder={isEdit && editTarget?.smtp_has_password ? '••••••••' : 'SMTP-Passwort'}
                value={form.smtp_password}
                onChange={(e) => setForm({ ...form, smtp_password: e.target.value })}
              />
              {isEdit && editTarget?.smtp_has_password && (
                <p className="text-xs text-muted-foreground mt-1">
                  Gespeicherte Passwörter werden nicht angezeigt. Leer lassen um das bestehende Passwort zu behalten.
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Absendername (optional)" placeholder={form.display_name || 'Absendername'} value={form.smtp_from_name}
                onChange={(e) => setForm({ ...form, smtp_from_name: e.target.value })} />
              <div className="flex items-center gap-3 pt-6">
                <Switch checked={form.smtp_secure} onCheckedChange={(v) => setForm({ ...form, smtp_secure: v })} />
                <span className="text-sm">TLS/SSL</span>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2">
          <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
          <span className="text-sm">Aktiv</span>
        </div>

        <div className="flex gap-2 justify-end">
          <Button variant="ghost" size="sm" onClick={onClose}>Abbrechen</Button>
          <Button variant="primary" size="sm" disabled={!isFormValid(form) || isPending} onClick={onSave}>
            {isPending ? 'Speichere…' : isEdit ? 'Speichern' : 'Anlegen'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

function isFormValid(f: MailboxFormState) {
  if (!f.display_name.trim() || !f.email_address.trim()) return false
  if (f.transport_type === 'smtp') {
    if (!f.smtp_host.trim() || !f.smtp_port || !f.smtp_username.trim()) return false
  }
  if (f.transport_type === 'm365_app') {
    if (!f.m365_app_connection_id) return false
  }
  return true
}

export function SharedMailboxesTab() {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const authUser = useAuthStore((s) => s.user)
  const [selectedMailbox, setSelectedMailbox] = useState<SharedMailbox | null>(null)
  const [editTarget, setEditTarget] = useState<SharedMailbox | null>(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [form, setForm] = useState<MailboxFormState>(emptyForm())
  const [deleteTarget, setDeleteTarget] = useState<SharedMailbox | null>(null)
  const [addUserTarget, setAddUserTarget] = useState<number | null>(null)
  const [testSendTarget, setTestSendTarget] = useState<SharedMailbox | null>(null)
  const [testSendTo, setTestSendTo] = useState('')
  const [testSendResult, setTestSendResult] = useState<{ ok: true } | { error: string } | null>(null)
  const [testSmtpTarget, setTestSmtpTarget] = useState<SharedMailbox | null>(null)
  const [testSmtpTo, setTestSmtpTo] = useState('')
  const [testSmtpResult, setTestSmtpResult] = useState<{ ok: true } | { error: string } | null>(null)
  const [testM365AppTarget, setTestM365AppTarget] = useState<SharedMailbox | null>(null)
  const [testM365AppTo, setTestM365AppTo] = useState('')
  const [testM365AppResult, setTestM365AppResult] = useState<{ ok: true } | { error: string } | null>(null)
  const [showAppConnections, setShowAppConnections] = useState(false)
  const [showAddAppConn, setShowAddAppConn] = useState(false)
  const [appConnForm, setAppConnForm] = useState({ display_name: '', tenant_id: '', client_id: '', client_secret: '', is_active: true })
  const [deleteAppConnTarget, setDeleteAppConnTarget] = useState<M365AppConnection | null>(null)

  const { data: mailboxes = [], isLoading, error } = useQuery<SharedMailbox[]>({
    queryKey: ['shared-mailboxes'],
    queryFn: async () => {
      const { data } = await apiClient.get('/settings/shared-mailboxes')
      return data
    },
  })

  const { data: permissions = [], isLoading: permsLoading } = useQuery<MailboxPermission[]>({
    queryKey: ['shared-mailbox-permissions', selectedMailbox?.id],
    queryFn: async () => {
      const { data } = await apiClient.get(`/settings/shared-mailboxes/${selectedMailbox!.id}/permissions`)
      return data
    },
    enabled: !!selectedMailbox,
  })

  const { data: allUsers = [] } = useQuery<User[]>({
    queryKey: ['users-list', 'internal'],
    queryFn: async () => {
      const { data } = await apiClient.get('/users?portalKind=internal')
      return data
    },
    enabled: !!selectedMailbox,
  })

  const { data: appConnections = [] } = useQuery<M365AppConnection[]>({
    queryKey: ['m365-app-connections'],
    queryFn: async () => {
      const { data } = await apiClient.get('/settings/m365-app-connections')
      return data
    },
  })

  function buildPayload(f: MailboxFormState) {
    const base: Record<string, unknown> = {
      display_name: f.display_name.trim(),
      email_address: f.email_address.trim(),
      is_active: f.is_active,
      transport_type: f.transport_type,
    }
    if (f.transport_type === 'smtp') {
      base.smtp_host = f.smtp_host.trim() || null
      base.smtp_port = f.smtp_port ? parseInt(f.smtp_port, 10) : null
      base.smtp_username = f.smtp_username.trim() || null
      base.smtp_secure = f.smtp_secure
      base.smtp_from_name = f.smtp_from_name.trim() || null
      if (f.smtp_password) base.smtp_password = f.smtp_password
    }
    if (f.transport_type === 'm365_app') {
      base.m365_app_connection_id = f.m365_app_connection_id ? parseInt(f.m365_app_connection_id, 10) : null
      base.m365_mailbox_address = f.m365_mailbox_address.trim() || null
    }
    return base
  }

  const createMutation = useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post('/settings/shared-mailboxes', buildPayload(form))
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shared-mailboxes'] })
      setShowAddModal(false)
      setForm(emptyForm())
      toast({ title: 'Postfach angelegt', variant: 'success' })
    },
    onError: (err: any) => {
      toast({ title: err?.response?.data?.message ?? 'Fehler beim Anlegen', tone: 'danger' })
    },
  })

  const updateMutation = useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.patch(`/settings/shared-mailboxes/${editTarget!.id}`, buildPayload(form))
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shared-mailboxes'] })
      setEditTarget(null)
      setForm(emptyForm())
      toast({ title: 'Postfach gespeichert', variant: 'success' })
    },
    onError: (err: any) => {
      toast({ title: err?.response?.data?.message ?? 'Fehler beim Speichern', variant: 'error' })
    },
  })

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: number; is_active: boolean }) => {
      await apiClient.patch(`/settings/shared-mailboxes/${id}`, { is_active })
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['shared-mailboxes'] }),
    onError: () => toast({ title: 'Fehler beim Speichern', tone: 'danger' }),
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/settings/shared-mailboxes/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shared-mailboxes'] })
      if (selectedMailbox?.id === deleteTarget?.id) setSelectedMailbox(null)
      setDeleteTarget(null)
      toast({ title: 'Postfach gelöscht', variant: 'success' })
    },
    onError: () => toast({ title: 'Fehler beim Löschen', tone: 'danger' }),
  })

  const addPermissionMutation = useMutation({
    mutationFn: async (userId: string) => {
      await apiClient.post(`/settings/shared-mailboxes/${selectedMailbox!.id}/permissions`, {
        user_id: userId, can_read: true, can_send: false,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shared-mailbox-permissions', selectedMailbox?.id] })
      setAddUserTarget(null)
      toast({ title: 'Zugriff erteilt', tone: 'success' })
    },
    onError: () => toast({ title: 'Fehler beim Erteilen des Zugriffs', tone: 'danger' }),
  })

  const updatePermissionMutation = useMutation({
    mutationFn: async ({ userId, can_read, can_send }: { userId: string; can_read: boolean; can_send: boolean }) => {
      await apiClient.post(`/settings/shared-mailboxes/${selectedMailbox!.id}/permissions`, {
        user_id: userId, can_read, can_send,
      })
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['shared-mailbox-permissions', selectedMailbox?.id] }),
    onError: () => toast({ title: 'Fehler beim Speichern', tone: 'danger' }),
  })

  const removePermissionMutation = useMutation({
    mutationFn: async (userId: string) => {
      await apiClient.delete(`/settings/shared-mailboxes/${selectedMailbox!.id}/permissions/${userId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shared-mailbox-permissions', selectedMailbox?.id] })
      toast({ title: 'Zugriff entzogen', tone: 'success' })
    },
    onError: () => toast({ title: 'Fehler beim Entziehen des Zugriffs', tone: 'danger' }),
  })

  const addMeAsSenderMutation = useMutation({
    mutationFn: async (userId: string) => {
      await apiClient.post(`/settings/shared-mailboxes/${selectedMailbox!.id}/permissions`, {
        user_id: userId, can_read: true, can_send: true,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shared-mailbox-permissions', selectedMailbox?.id] })
      toast({ title: 'Sendeerlaubnis erteilt – du kannst jetzt E-Mails über dieses Postfach senden.', variant: 'success' })
    },
    onError: () => toast({ title: 'Fehler beim Erteilen der Sendeerlaubnis', variant: 'error' }),
  })

  const testSendMutation = useMutation({
    mutationFn: async ({ mailboxId, to }: { mailboxId: number; to: string }) => {
      const { data } = await apiClient.post(`/settings/shared-mailboxes/${mailboxId}/test-send`, { to })
      return data
    },
    onSuccess: () => setTestSendResult({ ok: true }),
    onError: (err: any) => setTestSendResult({ error: err?.response?.data?.message ?? 'Unbekannter Fehler' }),
  })

  const testSmtpMutation = useMutation({
    mutationFn: async ({ mailboxId, to }: { mailboxId: number; to: string }) => {
      const { data } = await apiClient.post(`/settings/shared-mailboxes/${mailboxId}/test-smtp`, { to })
      return data
    },
    onSuccess: () => {
      setTestSmtpResult({ ok: true })
      queryClient.invalidateQueries({ queryKey: ['shared-mailboxes'] })
    },
    onError: (err: any) => {
      setTestSmtpResult({ error: err?.response?.data?.message ?? 'Unbekannter Fehler' })
      queryClient.invalidateQueries({ queryKey: ['shared-mailboxes'] })
    },
  })

  const testM365AppMutation = useMutation({
    mutationFn: async ({ mailboxId, to }: { mailboxId: number; to: string }) => {
      const { data } = await apiClient.post(`/settings/shared-mailboxes/${mailboxId}/test-m365-app`, { to })
      return data
    },
    onSuccess: () => {
      setTestM365AppResult({ ok: true })
      queryClient.invalidateQueries({ queryKey: ['shared-mailboxes'] })
    },
    onError: (err: any) => {
      setTestM365AppResult({ error: err?.response?.data?.message ?? 'Unbekannter Fehler' })
      queryClient.invalidateQueries({ queryKey: ['shared-mailboxes'] })
    },
  })

  const createAppConnMutation = useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post('/settings/m365-app-connections', appConnForm)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['m365-app-connections'] })
      setShowAddAppConn(false)
      setAppConnForm({ display_name: '', tenant_id: '', client_id: '', client_secret: '', is_active: true })
      toast({ title: 'App-Verbindung angelegt', variant: 'success' })
    },
    onError: (err: any) => toast({ title: err?.response?.data?.message ?? 'Fehler beim Anlegen', variant: 'error' }),
  })

  const deleteAppConnMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/settings/m365-app-connections/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['m365-app-connections'] })
      setDeleteAppConnTarget(null)
      toast({ title: 'App-Verbindung gelöscht', variant: 'success' })
    },
    onError: () => toast({ title: 'Fehler beim Löschen', variant: 'error' }),
  })

  const testAppConnMutation = useMutation({
    mutationFn: async (id: number) => {
      const { data } = await apiClient.post(`/settings/m365-app-connections/${id}/test`, {})
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['m365-app-connections'] })
      toast({ title: 'Verbindungstest erfolgreich', variant: 'success' })
    },
    onError: (err: any) => {
      queryClient.invalidateQueries({ queryKey: ['m365-app-connections'] })
      toast({ title: err?.response?.data?.message ?? 'Verbindungstest fehlgeschlagen', variant: 'error' })
    },
  })

  const mailboxColumns: ColumnDef<SharedMailbox>[] = [
    {
      accessorKey: 'display_name',
      header: 'Anzeigename',
      cell: ({ row }) => (
        <button
          className="text-left font-medium text-brand-600 hover:underline"
          onClick={() => setSelectedMailbox(row.original)}
        >
          {row.original.display_name}
        </button>
      ),
    },
    { accessorKey: 'email_address', header: 'E-Mail-Adresse' },
    {
      accessorKey: 'transport_type',
      header: 'Versandart',
      cell: ({ row }) => {
        const t = row.original.transport_type
        const label = t === 'smtp' ? 'SMTP' : t === 'm365_app' ? 'M365 App' : 'Microsoft 365'
        const cls = t === 'smtp'
          ? 'bg-blue-100 text-blue-800'
          : t === 'm365_app'
            ? 'bg-green-100 text-green-800'
            : 'bg-purple-100 text-purple-800'
        return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cls}`}>{label}</span>
      },
    },
    {
      accessorKey: 'is_active',
      header: 'Aktiv',
      cell: ({ row }) => (
        <Switch
          checked={row.original.is_active}
          onCheckedChange={(checked) => toggleActiveMutation.mutate({ id: row.original.id, is_active: checked })}
        />
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex items-center gap-2 justify-end">
          <Button variant="ghost" size="xs" onClick={() => setSelectedMailbox(row.original)}>
            <Users className="h-4 w-4 mr-1" />
            Benutzer
          </Button>
          <Button
            variant="ghost"
            size="xs"
            onClick={() => {
              setEditTarget(row.original)
              setForm(emptyForm(row.original))
            }}
          >
            <Settings2 className="h-4 w-4 mr-1" />
            Konfigurieren
          </Button>
          {row.original.transport_type === 'smtp' ? (
            <Button
              variant="ghost"
              size="xs"
              onClick={() => {
                setTestSmtpTarget(row.original)
                setTestSmtpTo('')
                setTestSmtpResult(null)
              }}
            >
              <Send className="h-4 w-4 mr-1" />
              SMTP-Test
            </Button>
          ) : row.original.transport_type === 'm365_app' ? (
            <Button
              variant="ghost"
              size="xs"
              onClick={() => {
                setTestM365AppTarget(row.original)
                setTestM365AppTo('')
                setTestM365AppResult(null)
              }}
            >
              <Send className="h-4 w-4 mr-1" />
              App-Test
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="xs"
              onClick={() => {
                setTestSendTarget(row.original)
                setTestSendTo('')
                setTestSendResult(null)
              }}
            >
              <Send className="h-4 w-4 mr-1" />
              Test
            </Button>
          )}
          <Button variant="ghost" size="xs" onClick={() => setDeleteTarget(row.original)}>
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </div>
      ),
    },
  ]

  const permissionColumns: ColumnDef<MailboxPermission>[] = [
    {
      accessorKey: 'user_name',
      header: 'Benutzer',
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.user_name}</div>
          <div className="text-xs text-muted-foreground">{row.original.user_email}</div>
        </div>
      ),
    },
    {
      accessorKey: 'can_read',
      header: 'Lesen',
      cell: ({ row }) => (
        <Switch
          checked={row.original.can_read}
          onCheckedChange={(checked) => updatePermissionMutation.mutate({
            userId: row.original.user_id, can_read: checked, can_send: row.original.can_send,
          })}
        />
      ),
    },
    {
      accessorKey: 'can_send',
      header: 'Senden',
      cell: ({ row }) => (
        <Switch
          checked={row.original.can_send}
          onCheckedChange={(checked) => updatePermissionMutation.mutate({
            userId: row.original.user_id, can_read: row.original.can_read, can_send: checked,
          })}
        />
      ),
    },
    {
      id: 'remove',
      header: '',
      cell: ({ row }) => (
        <Button variant="ghost" size="xs" onClick={() => removePermissionMutation.mutate(row.original.user_id)}>
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      ),
    },
  ]

  const usersWithoutAccess = allUsers.filter((u) => !permissions.some((p) => p.user_id === u.id))

  const meInUserList = allUsers.find((u) => u.email === authUser?.email)
  const myPerm = meInUserList ? permissions.find((p) => p.user_id === meInUserList.id) : null
  const showAddMeAsSender = !!meInUserList && (!myPerm || !myPerm.can_send) && selectedMailbox?.transport_type === 'graph'

  if (isLoading) return <LoadingState />
  if (error) return <ErrorState title="Fehler beim Laden der Shared Mailboxes" />

  // ─── Permission detail view ───────────────────────────────────────────────
  if (selectedMailbox) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setSelectedMailbox(null)}>
            <ChevronLeft className="h-4 w-4 mr-1" />
            Zurück
          </Button>
          <div>
            <h2 className="text-h3">{selectedMailbox.display_name}</h2>
            <p className="text-sm text-muted-foreground">{selectedMailbox.email_address}</p>
          </div>
        </div>

        {showAddMeAsSender && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-amber-900">Du hast keine Sendeerlaubnis für dieses Postfach</p>
              <p className="text-xs text-amber-700 mt-0.5">
                Füge dich als Sender hinzu, damit E-Mails über dieses Postfach mit deinem Microsoft-Account versendet werden können.
              </p>
            </div>
            <Button
              size="sm"
              variant="primary"
              onClick={() => meInUserList && addMeAsSenderMutation.mutate(meInUserList.id)}
              disabled={addMeAsSenderMutation.isPending}
            >
              <Plus className="h-4 w-4 mr-1" />
              {addMeAsSenderMutation.isPending ? 'Füge hinzu…' : 'Mich hinzufügen'}
            </Button>
          </div>
        )}

        <Card>
          <Card.Header>
            <div className="flex items-center justify-between">
              <span className="font-medium">Benutzerrechte</span>
              {usersWithoutAccess.length > 0 && (
                <div className="flex items-center gap-2">
                  {addUserTarget !== null ? (
                    <>
                      <select
                        className="border rounded px-2 py-1 text-sm"
                        value={addUserTarget ?? ''}
                        onChange={(e) => setAddUserTarget(Number(e.target.value))}
                      >
                        <option value="">Benutzer wählen…</option>
                        {usersWithoutAccess.map((u) => (
                          <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                        ))}
                      </select>
                      <Button size="sm" variant="primary" disabled={!addUserTarget}
                        onClick={() => addUserTarget && addPermissionMutation.mutate(String(addUserTarget))}>
                        Hinzufügen
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setAddUserTarget(null)}>Abbrechen</Button>
                    </>
                  ) : (
                    <Button size="sm" variant="secondary" onClick={() => setAddUserTarget(0)}>
                      <Plus className="h-4 w-4 mr-1" />
                      Benutzer hinzufügen
                    </Button>
                  )}
                </div>
              )}
            </div>
          </Card.Header>
          <Card.Body>
            {permsLoading ? <LoadingState /> : permissions.length === 0 ? (
              <EmptyState title="Keine Benutzer mit Zugriff"
                description="Füge Benutzer hinzu, um ihnen Zugriff auf diese Mailbox zu gewähren." />
            ) : (
              <DataTable columns={permissionColumns} data={permissions} />
            )}
          </Card.Body>
        </Card>
      </div>
    )
  }

  // ─── Mailbox list view ────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      <Card>
        <Card.Header>
          <div className="flex items-center justify-between">
            <span className="font-medium">Zentrale Postfächer</span>
            <Button size="sm" variant="primary" onClick={() => { setForm(emptyForm()); setShowAddModal(true) }}>
              <Plus className="h-4 w-4 mr-1" />
              Postfach hinzufügen
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {mailboxes.length === 0 ? (
            <EmptyState title="Keine Postfächer konfiguriert"
              description="Lege zentrale Postfächer an, um Benutzern Zugriff zu gewähren." />
          ) : (
            <DataTable columns={mailboxColumns} data={mailboxes} />
          )}
        </Card.Body>
      </Card>

      {/* Add modal */}
      <MailboxFormModal
        open={showAddModal}
        onClose={() => { setShowAddModal(false); setForm(emptyForm()) }}
        title="Neues zentrales Postfach"
        onSave={() => createMutation.mutate()}
        isPending={createMutation.isPending}
        isEdit={false}
        form={form}
        setForm={setForm}
        editTarget={editTarget}
        appConnections={appConnections}
      />

      {/* Edit modal */}
      <MailboxFormModal
        open={!!editTarget}
        onClose={() => { setEditTarget(null); setForm(emptyForm()) }}
        title={`Postfach konfigurieren – ${editTarget?.display_name ?? ''}`}
        onSave={() => updateMutation.mutate()}
        isPending={updateMutation.isPending}
        isEdit={true}
        form={form}
        setForm={setForm}
        editTarget={editTarget}
        appConnections={appConnections}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Shared Mailbox löschen"
        message={`Möchtest du „${deleteTarget?.display_name}" (${deleteTarget?.email_address}) wirklich löschen? Alle Benutzerrechte werden ebenfalls entfernt.`}
        confirmLabel="Löschen"
        variant="danger"
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* M365 Test-Send modal */}
      <Modal
        open={!!testSendTarget}
        onClose={() => { setTestSendTarget(null); setTestSendResult(null) }}
        title={`Test-E-Mail senden – ${testSendTarget?.display_name ?? ''}`}
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Sendet eine Test-E-Mail über{' '}
            <span className="font-medium">{testSendTarget?.email_address}</span> (Microsoft 365).
            Dein Account muss Sendeerlaubnis für dieses Postfach besitzen.
          </p>
          <Input label="Empfänger" type="email" placeholder="empfaenger@beispiel.de"
            value={testSendTo}
            onChange={(e) => { setTestSendTo(e.target.value); setTestSendResult(null) }} />
          {testSendResult && (
            <div className={`rounded p-3 text-sm ${'ok' in testSendResult ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
              {'ok' in testSendResult ? 'E-Mail erfolgreich gesendet.' : `Fehler: ${testSendResult.error}`}
            </div>
          )}
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" size="sm" onClick={() => { setTestSendTarget(null); setTestSendResult(null) }}>
              Schließen
            </Button>
            <Button variant="primary" size="sm"
              disabled={!testSendTo.trim() || testSendMutation.isPending}
              onClick={() => testSendTarget && testSendMutation.mutate({ mailboxId: testSendTarget.id, to: testSendTo.trim() })}>
              <Send className="h-4 w-4 mr-1" />
              {testSendMutation.isPending ? 'Sende…' : 'Senden'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* SMTP Test modal */}
      <Modal
        open={!!testSmtpTarget}
        onClose={() => { setTestSmtpTarget(null); setTestSmtpResult(null) }}
        title={`SMTP-Test – ${testSmtpTarget?.display_name ?? ''}`}
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Testet den SMTP-Versand über{' '}
            <span className="font-medium">{testSmtpTarget?.email_address}</span>.
            Das Ergebnis wird am Postfach gespeichert.
          </p>
          {testSmtpTarget && !testSmtpTarget.smtp_has_password && (
            <div className="rounded p-3 text-sm bg-amber-50 text-amber-800 border border-amber-200">
              Dieses Postfach hat noch kein SMTP-Passwort hinterlegt. Bitte zuerst konfigurieren.
            </div>
          )}
          <Input label="Empfänger (Testadresse)" type="email" placeholder="empfaenger@beispiel.de"
            value={testSmtpTo}
            onChange={(e) => { setTestSmtpTo(e.target.value); setTestSmtpResult(null) }} />
          {testSmtpResult && (
            <div className={`rounded p-3 text-sm ${'ok' in testSmtpResult ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
              {'ok' in testSmtpResult ? 'SMTP-Test erfolgreich. E-Mail wurde gesendet.' : `Fehler: ${testSmtpResult.error}`}
            </div>
          )}
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" size="sm" onClick={() => { setTestSmtpTarget(null); setTestSmtpResult(null) }}>
              Schließen
            </Button>
            <Button variant="primary" size="sm"
              disabled={!testSmtpTo.trim() || testSmtpMutation.isPending || (testSmtpTarget ? !testSmtpTarget.smtp_has_password : false)}
              onClick={() => testSmtpTarget && testSmtpMutation.mutate({ mailboxId: testSmtpTarget.id, to: testSmtpTo.trim() })}>
              <Send className="h-4 w-4 mr-1" />
              {testSmtpMutation.isPending ? 'Teste…' : 'SMTP testen'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* M365 App Test-Send modal */}
      <Modal
        open={!!testM365AppTarget}
        onClose={() => { setTestM365AppTarget(null); setTestM365AppResult(null) }}
        title={`M365-App-Test – ${testM365AppTarget?.display_name ?? ''}`}
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Sendet eine Test-E-Mail über{' '}
            <span className="font-medium">{testM365AppTarget?.m365_mailbox_address || testM365AppTarget?.email_address}</span>{' '}
            via Microsoft Graph Application Permission.
            Das Ergebnis wird am Postfach gespeichert.
          </p>
          <Input label="Empfänger (Testadresse)" type="email" placeholder="empfaenger@beispiel.de"
            value={testM365AppTo}
            onChange={(e) => { setTestM365AppTo(e.target.value); setTestM365AppResult(null) }} />
          {testM365AppResult && (
            <div className={`rounded p-3 text-sm ${'ok' in testM365AppResult ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
              {'ok' in testM365AppResult ? 'M365-App-Test erfolgreich. E-Mail wurde gesendet.' : `Fehler: ${testM365AppResult.error}`}
            </div>
          )}
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" size="sm" onClick={() => { setTestM365AppTarget(null); setTestM365AppResult(null) }}>
              Schließen
            </Button>
            <Button variant="primary" size="sm"
              disabled={!testM365AppTo.trim() || testM365AppMutation.isPending}
              onClick={() => testM365AppTarget && testM365AppMutation.mutate({ mailboxId: testM365AppTarget.id, to: testM365AppTo.trim() })}>
              <Send className="h-4 w-4 mr-1" />
              {testM365AppMutation.isPending ? 'Teste…' : 'App-Test senden'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* M365 App Connections management Card */}
      <Card>
        <Card.Header>
          <div className="flex items-center justify-between">
            <button
              className="font-medium flex items-center gap-2 hover:text-brand-600"
              onClick={() => setShowAppConnections((v) => !v)}
            >
              Microsoft 365 App-Verbindungen
              <span className="text-xs text-muted-foreground">({appConnections.length})</span>
              <span className="text-xs text-muted-foreground">{showAppConnections ? '▲' : '▼'}</span>
            </button>
            <Button size="sm" variant="secondary" onClick={() => { setShowAppConnections(true); setShowAddAppConn(true) }}>
              <Plus className="h-4 w-4 mr-1" />
              App-Verbindung anlegen
            </Button>
          </div>
        </Card.Header>
        {showAppConnections && (
          <Card.Body>
            {appConnections.length === 0 ? (
              <EmptyState
                title="Keine App-Verbindungen"
                description="Lege eine Microsoft Azure App Registration an und hinterlege sie hier. Die App benötigt die Graph-Permission Mail.Send (Application)."
              />
            ) : (
              <DataTable
                columns={[
                  { accessorKey: 'display_name', header: 'Name' },
                  { accessorKey: 'tenant_id', header: 'Tenant-ID' },
                  { accessorKey: 'client_id', header: 'Client-ID' },
                  {
                    accessorKey: 'is_active',
                    header: 'Aktiv',
                    cell: ({ row }) => (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${row.original.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
                        {row.original.is_active ? 'Aktiv' : 'Inaktiv'}
                      </span>
                    ),
                  },
                  {
                    accessorKey: 'last_test_success',
                    header: 'Letzter Test',
                    cell: ({ row }) => {
                      const { last_test_success, last_test_at, last_test_error } = row.original
                      if (last_test_at === null) return <span className="text-xs text-muted-foreground">–</span>
                      return (
                        <span title={last_test_error ?? undefined} className={`text-xs px-2 py-0.5 rounded-full ${last_test_success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {last_test_success ? 'OK' : 'Fehler'}
                        </span>
                      )
                    },
                  },
                  {
                    id: 'conn-actions',
                    header: '',
                    cell: ({ row }) => (
                      <div className="flex items-center gap-2 justify-end">
                        <Button variant="ghost" size="xs"
                          disabled={testAppConnMutation.isPending}
                          onClick={() => testAppConnMutation.mutate(row.original.id)}>
                          Verbindung testen
                        </Button>
                        <Button variant="ghost" size="xs" onClick={() => setDeleteAppConnTarget(row.original)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    ),
                  },
                ] satisfies ColumnDef<M365AppConnection>[]}
                data={appConnections}
              />
            )}
          </Card.Body>
        )}
      </Card>

      {/* Add App Connection modal */}
      <Modal open={showAddAppConn} onClose={() => setShowAddAppConn(false)} title="App-Verbindung anlegen">
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Hinterlege eine Azure App Registration mit der Graph-Permission <strong>Mail.Send (Application)</strong>.
            Das Client-Secret wird verschlüsselt gespeichert und nie wieder angezeigt.
          </p>
          <Input label="Anzeigename" placeholder="z.B. Implec M365 App" value={appConnForm.display_name}
            onChange={(e) => setAppConnForm({ ...appConnForm, display_name: e.target.value })} />
          <Input label="Tenant-ID (Directory-ID)" placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" value={appConnForm.tenant_id}
            onChange={(e) => setAppConnForm({ ...appConnForm, tenant_id: e.target.value })} />
          <Input label="Client-ID (Application-ID)" placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" value={appConnForm.client_id}
            onChange={(e) => setAppConnForm({ ...appConnForm, client_id: e.target.value })} />
          <Input label="Client-Secret" type="password" placeholder="Client-Secret der App Registration" value={appConnForm.client_secret}
            onChange={(e) => setAppConnForm({ ...appConnForm, client_secret: e.target.value })} />
          <div className="flex items-center gap-2">
            <Switch checked={appConnForm.is_active} onCheckedChange={(v) => setAppConnForm({ ...appConnForm, is_active: v })} />
            <span className="text-sm">Aktiv</span>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" size="sm" onClick={() => setShowAddAppConn(false)}>Abbrechen</Button>
            <Button variant="primary" size="sm"
              disabled={!appConnForm.display_name.trim() || !appConnForm.tenant_id.trim() || !appConnForm.client_id.trim() || !appConnForm.client_secret.trim() || createAppConnMutation.isPending}
              onClick={() => createAppConnMutation.mutate()}>
              {createAppConnMutation.isPending ? 'Speichere…' : 'Anlegen'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete App Connection confirm */}
      <ConfirmDialog
        open={!!deleteAppConnTarget}
        title="App-Verbindung löschen"
        description={`Möchtest du die App-Verbindung „${deleteAppConnTarget?.display_name}" wirklich löschen? Postfächer, die diese Verbindung verwenden, können danach keine E-Mails mehr senden.`}
        confirmLabel="Löschen"
        variant="danger"
        onConfirm={() => deleteAppConnTarget && deleteAppConnMutation.mutate(deleteAppConnTarget.id)}
        onCancel={() => setDeleteAppConnTarget(null)}
      />
    </div>
  )
}
